import {
  Box,
  CardMedia,
  Dialog,
  IconButton,
  Slide,
  Typography,
  useTheme,
} from "@mui/material";
import { ItemContributor } from "./ItemContributor";
import { Contributors } from "../../interfaces/Contributors";
import { Close, Globe, Instagram, Phone } from "../../icons";
import React from "react";
import { TransitionProps } from "@mui/material/transitions";

type ModalContributorProps = {
  data: Contributors[];
  open: boolean;
  onClose: () => void;
};


const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const ModalContributor = ({
  data,
  open,
  onClose,
}: ModalContributorProps) => {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slots={{
          transition: Transition,
        }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      slotProps={{
        paper: {
          style: {
            marginInline: 10,
            scrollbarColor: `${theme.palette.customColor?.dark} ${theme.palette.customColor?.main}`,
            width: 600,
            borderRadius: 15,
            backgroundColor: theme.palette.customColor?.main,
            backdropFilter: "saturate(180%) blur(20px)",
          },
        },
      }}
    >
      {data.map((s) => (
        <Box padding={5} key={s.id} position="relative">
          <IconButton
            onClick={onClose}
            sx={{
              borderRadius: "100vmax !important",
              position: "absolute",
              top: 5,
              right: 5,
            }}
          >
            <Close
              sx={{
                width: { xs: 25, md: 30 },
                height: { xs: 25, md: 30 },
              }}
            />
          </IconButton>

          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap={10}
          >
            <Box zIndex={-1} position="relative">
              <Box display="flex" justifyContent="center">
                <CardMedia
                  sx={{ maxWidth: 300, maxHeight: 400, borderRadius: 3 }}
                  component="img"
                  image={s.image}
                />
              </Box>

              <Box
                width="100%"
                position="absolute"
                boxShadow={`0px 0px 20px 30px ${theme.palette.customColor?.main}`}
              />
            </Box>

            <Box>
              <Box>
                <Typography
                  mt={8}
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: theme.palette.secondary[700] }}
                >
                  {s.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.secondary[100] }}
                >
                  {s.market}
                </Typography>
              </Box>

              <Box display="flex" gap={3} flexDirection="column" mt={5}>
                {s.education && (
                  <ItemContributor
                    primaryText="Education"
                    secondaryText={s.education}
                  />
                )}

                {s.country && (
                  <ItemContributor
                    primaryText="Country of nationality"
                    secondaryText={s.country}
                  />
                )}
                {s.birth && (
                  <ItemContributor
                    primaryText="Date of birth"
                    secondaryText={s.birth}
                  />
                )}

                <ItemContributor
                  primaryText="Instagram"
                  secondaryText={s.instagram.name}
                  secondaryUrl={s.instagram.url}
                  icon={
                    <Instagram
                      sx={{ width: 12, height: 12 }}
                      htmlColor={theme.palette.primary[500]}
                    />
                  }
                />
              </Box>
            </Box>
          </Box>

          {s.descriptionHtml && (
            <Box mt={{ xs: 5, md: 0 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: theme.palette.secondary[700], lineHeight: 0 }}
              >
                Biography
              </Typography>
              <Box
                dangerouslySetInnerHTML={{
                  __html: s.descriptionHtml ?? "",
                }}
              />
            </Box>
          )}

          {s.accomplishmentsHtml && (
            <Typography
              variant="caption"
              fontWeight={700}
              lineHeight={3}
              sx={{ color: theme.palette.secondary[700] }}
            >
              Accomplishments
            </Typography>
          )}

          <Box
            dangerouslySetInnerHTML={{
              __html: s.accomplishmentsHtml ?? "",
            }}
          />

          <Box
            mt={10}
            display="flex"
            flexWrap="wrap"
            gap={5}
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Box display="flex" flexDirection="column" gap={2}>
              <ItemContributor
                primaryText={
                  s.company.office === "" ? s.company.name : s.company.office
                }
                secondaryText={s.company.address}
              />

              {s.company.tel && (
                <ItemContributor
                  primaryText="Tel"
                  secondaryUrl={`tel:${s.company.tel}`}
                  secondaryText={s.company.tel}
                  icon={
                    <Phone
                      sx={{ width: 12, height: 12 }}
                      htmlColor={theme.palette.primary[500]}
                    />
                  }
                />
              )}

              {s.company.website && (
                <ItemContributor
                  primaryText="Website"
                  secondaryUrl={s.company.website}
                  secondaryText={s.company.website}
                  icon={
                    <Globe
                      sx={{ width: 12, height: 12 }}
                      htmlColor={theme.palette.primary[500]}
                    />
                  }
                />
              )}
            </Box>

            <Box
              mt={5}
              display={s.company.logo ? "flex" : "none"}
              width="100%"
              justifyContent="center"
            >
              <CardMedia
                component="img"
                src={s.company.logo}
                loading="lazy"
                alt=""
                sx={{
                  aspectRatio: 5 / 2,
                  maxWidth: 200,
                  maxHeight: { xs: 40, md: 50 },
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>
        </Box>
      ))}
    </Dialog>
  );
};
